import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { CountrySelect } from '@/components/ui/country-select'
import {
  getSubscriptionPlansApi,
  createSubscriptionPlanApi,
  updateSubscriptionPlanApi,
  deleteSubscriptionPlanApi,
  type SubscriptionPlan,
} from '@/lib/billing'
import { getCountriesApi, type Country } from '@/lib/api'
import {
  LoaderIcon,
  TagIcon,
  PlusIcon,
  FileEditIcon,
  Trash2Icon,
  CheckCircleIcon,
  GlobeIcon,
  BanknoteIcon,
} from 'lucide-react'


export const Route = createFileRoute('/dashboard/admin/subscription-plans')({
  component: () => {
    const [plans, setPlans] = useState<SubscriptionPlan[]>([])
    const [countries, setCountries] = useState<Country[]>([])
    const [loading, setLoading] = useState(true)

    // Create/Edit modal
    const [showModal, setShowModal] = useState(false)
    const [editPlan, setEditPlan] = useState<SubscriptionPlan | null>(null)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState({
      plan_name: '',
      country_id: 0,
      price_per_user_cents: 0,
      minimum_monthly_cents: 0,
      currency: 'EUR',
      is_active: true,
    })
    /**
     * Single source of truth for the empty-country error. The error is
     * ONLY shown after the user has tried to Save with the picker still
     * reset to "Select a country…" (which `CountrySelect` maps to
     * `country_id: 0`). Deriving avoids stale-state bugs that a manual
     * reset hook in 3 different places would otherwise need (modal close,
     * modal re-open, onChange clear). Reset implicitly by toggling
     * `saveAttempted` back to `false` on a clean form remount.
     */
    const [saveAttempted, setSaveAttempted] = useState(false)

    // Delete confirmation
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
      let cancelled = false
      async function load() {
        try {
          const [plansRes, countriesRes] = await Promise.all([
            getSubscriptionPlansApi(),
            getCountriesApi(),
          ])
          if (!cancelled) {
            setPlans(plansRes.data)
            setCountries(countriesRes.data)
          }
        } catch (err) {
          if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load data')
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [])

    const openCreate = () => {
      setEditPlan(null)
      setForm({ plan_name: '', country_id: 0, price_per_user_cents: 0, minimum_monthly_cents: 0, currency: 'EUR', is_active: true })
      setSaveAttempted(false) // fresh form, no prior validation attempts
      setShowModal(true)
    }

    const openEdit = (plan: SubscriptionPlan) => {
      setEditPlan(plan)
      setForm({
        plan_name: plan.plan_name,
        country_id: plan.country_id,
        price_per_user_cents: plan.price_per_user_cents,
        minimum_monthly_cents: plan.minimum_monthly_cents,
        currency: plan.currency,
        is_active: plan.is_active,
      })
      setSaveAttempted(false) // editing an EXISTING plan starts clean
      setShowModal(true)
    }

    /**
     * Derived empty-country flag — only `true` once the user has
     * attempted to Save (regardless of subsequent changes to the
     * picker). This is the only state we carry; deriving means we
     * can't accidentally leave the inline message visible after the
     * user fixes their selection.
     */
    const countryMissingError = saveAttempted && form.country_id === 0

    const handleSave = async () => {
      if (!form.plan_name || !form.country_id) {
        setSaveAttempted(true) // flips derived error ON if country is still empty
        if (!form.plan_name) toast.error('Plan name is required.')
        return
      }
      setSaving(true)
      try {
        if (editPlan) {
          await updateSubscriptionPlanApi(editPlan.plan_id, form)
          setPlans((prev) => prev.map((p) => p.plan_id === editPlan.plan_id ? { ...p, ...form, country_name: countries.find(c => c.country_id === form.country_id)?.country || p.country_name } : p))
          toast.success('Plan updated successfully.')
        } else {
          await createSubscriptionPlanApi({ ...form, currency_symbol: undefined })
          toast.success('Plan created successfully.')
          const reload = await getSubscriptionPlansApi()
          setPlans(reload.data)
        }
        setShowModal(false)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save plan')
      } finally {
        setSaving(false)
      }
    }

    const handleDelete = async () => {
      if (confirmDelete == null) return
      setDeleting(true)
      try {
        await deleteSubscriptionPlanApi(confirmDelete)
        setPlans((prev) => prev.filter((p) => p.plan_id !== confirmDelete))
        toast.success('Plan deactivated.')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to deactivate plan')
      } finally {
        setDeleting(false)
        setConfirmDelete(null)
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Subscription plans</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading plans…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <div>
                <h1 className="text-2xl font-semibold">Subscription plans</h1>
                <p className="mt-1 text-sm text-muted-foreground">Manage pricing plans for each country.</p>
              </div>
            </div>
            <Button size="sm" onClick={openCreate} className="gap-1.5">
              <PlusIcon className="size-4" />New plan
            </Button>
          </div>
        </div>

        {plans.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <TagIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No plans configured</p>
            <p className="text-sm text-muted-foreground">Create your first subscription plan to get started.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <div key={plan.plan_id} className="rounded-xl border border-border bg-card p-5 transition-all hover:shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <TagIcon className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">{plan.plan_name}</p>
                      <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        plan.is_active ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'
                      }`}>
                        {plan.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <PriceRow icon={<BanknoteIcon className="size-3.5" />} label="Per user" value={formatCentsWithCurrency(plan.price_per_user_cents, plan.currency)} />
                  <PriceRow icon={<BanknoteIcon className="size-3.5" />} label="Minimum" value={formatCentsWithCurrency(plan.minimum_monthly_cents, plan.currency)} />
                  <PriceRow icon={<GlobeIcon className="size-3.5" />} label="Currency" value={plan.currency} />
                  {plan.country_name && <PriceRow icon={<GlobeIcon className="size-3.5" />} label="Country" value={plan.country_name} />}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(plan)} className="flex-1 gap-1">
                    <FileEditIcon className="size-3.5" />Edit
                  </Button>
                  {plan.is_active && (
                    <Button variant="outline" size="sm" onClick={() => setConfirmDelete(plan.plan_id)} className="gap-1 border-destructive/30 text-destructive hover:bg-destructive/10">
                      <Trash2Icon className="size-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog
          open={showModal}
          onClose={() => setShowModal(false)}
          title={editPlan ? 'Edit plan' : 'Create plan'}
          description={editPlan ? `Update pricing for ${editPlan.plan_name}` : 'Create a new subscription pricing plan.'}
          footer={
            <>
              <Button variant="outline" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !form.plan_name} className="gap-1.5">
                {saving ? <LoaderIcon className="size-4 animate-spin" /> : <CheckCircleIcon className="size-4" />}
                {editPlan ? 'Save changes' : 'Create plan'}
              </Button>
            </>
          }
        >            <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Plan name</label>
              <input type="text" value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                autoFocus
                placeholder="e.g. Standard Monthly" className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label htmlFor="planCountry" className="mb-1 block text-xs font-medium text-muted-foreground">Country</label>
              <CountrySelect
                id="planCountry"
                value={form.country_id}
                onChange={(v) => setForm({ ...form, country_id: v === '' ? 0 : v })}
                countries={countries}
                emptyLabel="Select a country…"
                aria-describedby="planCountry-error"
                aria-invalid={countryMissingError || undefined}
              />
              {countryMissingError && (
                <p
                  id="planCountry-error"
                  role="alert"
                  className="mt-1 text-xs text-destructive"
                >
                  Pick a country before saving.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Price per user (cents)</label>
                <input type="number" min={0} value={form.price_per_user_cents} onChange={(e) => setForm({ ...form, price_per_user_cents: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Minimum monthly (cents)</label>
                <input type="number" min={0} value={form.minimum_monthly_cents} onChange={(e) => setForm({ ...form, minimum_monthly_cents: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Currency</label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary">
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="NGN">NGN</option>
                  <option value="GHS">GHS</option>
                  <option value="MXN">MXN</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
                <div className="flex h-full items-center gap-3 pt-1.5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="rounded border-border text-primary focus:ring-primary" />
                    <span className="text-sm text-foreground">Active</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Dialog>

        {/* Delete confirmation */}
        <DestructiveConfirm
          open={confirmDelete != null}
          onClose={() => setConfirmDelete(null)}
          title="Deactivate plan?"
          description="This will soft-deactivate the plan. Organizations currently on this plan will retain it, but new subscriptions won't be able to use it."
          confirmLabel="Deactivate"
          onConfirm={handleDelete}
          loading={deleting}
        />
      </div>
    )
  },
})

function PriceRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-medium text-foreground tabular-nums">{value}</span>
    </div>
  )
}
